import EmberService, { service } from '@ember/service';

import type { TourConfig } from '@databox/types/domains/tour/tour';

import type { Coredata, Service } from 'webapp-types';
import { TourId } from 'webapp/enums/tour';
import type { UserSettingsKey } from 'webapp/enums/user-settings';

import type Tour from 'webapp/models-custom/tour';
import type { TourReactPayload } from 'webapp/models-custom/tour-react';
import type TourReact from 'webapp/models-custom/tour-react';
import ClientPerformanceTour from 'webapp/models-custom/tour/client-performance';
import ClientsSetupTour from 'webapp/models-custom/tour/clients-setup';
import CustomMetricsPopupQbTour from 'webapp/models-custom/tour/custom-metrics-popup-qb';
import CustomMetricsPopupScriptTour from 'webapp/models-custom/tour/custom-metrics-popup-script';
import DesignerAddToLoopDataboardTour from 'webapp/models-custom/tour/designer/add-to-loop-databoard';
import DesignerAddToReportTour from 'webapp/models-custom/tour/designer/add-to-report';
import DesignerChangeDateRangeTour from 'webapp/models-custom/tour/designer/change-date-range';
import DesignerDragAndDropTour from 'webapp/models-custom/tour/designer/drag-and-drop';
import DesignerIntroTour from 'webapp/models-custom/tour/designer/intro';
import DesignerMoreOptionTour from 'webapp/models-custom/tour/designer/more-option';
import DesignerMoveBlockTour from 'webapp/models-custom/tour/designer/move-block';
import DesignerOptionHiddenTour from 'webapp/models-custom/tour/designer/option-hidden';
import DesignerResizeBlockTour from 'webapp/models-custom/tour/designer/resize-block';
import DesignerSelectDimensionTour from 'webapp/models-custom/tour/designer/select-dimension';
import DesignerSelectVisualizationTypeTour from 'webapp/models-custom/tour/designer/select-visualization-type';
import DesignerSharableLinkTour from 'webapp/models-custom/tour/designer/sharable-link';
import FirstBoardEditTour from 'webapp/models-custom/tour/first-board-edit';
import FirstBoardShareTour from 'webapp/models-custom/tour/first-board-share';
import FirstBoardViewTour from 'webapp/models-custom/tour/first-board-view';
import GenieArtifactEditTour from 'webapp/models-custom/tour/genie/artifact-edit';
import GenieClientSelectorTour from 'webapp/models-custom/tour/genie/client-selector';
import GenieCustomizeTour from 'webapp/models-custom/tour/genie/customize';
import GenieSkillsTour from 'webapp/models-custom/tour/genie/skills';
import GuidedOnboardingDataManagerPopularTour from 'webapp/models-custom/tour/guided-onboarding/data-manager-popular';
import GuidedOnboardingDataManagerSelectedTour from 'webapp/models-custom/tour/guided-onboarding/data-manager-selected';
import GuidedOnboardingMetricsTour from 'webapp/models-custom/tour/guided-onboarding/metrics';
import GuidedOnboardingWizardTour from 'webapp/models-custom/tour/guided-onboarding/wizard';
import MetricsSourcesTour from 'webapp/models-custom/tour/metrics-sources';
import OnboardingConnectSourceTour from 'webapp/models-custom/tour/onboarding-connect-source';
import OnboardingSourceTour from 'webapp/models-custom/tour/onboarding-source';
import QbSheetsTour from 'webapp/models-custom/tour/qb-sheets';
import QbSheetsVideoTour from 'webapp/models-custom/tour/qb-sheets-video';
import RecentlyDeletedTour from 'webapp/models-custom/tour/recently-deleted';
import ReportsAddDataElementTour from 'webapp/models-custom/tour/reports/add-data-element';
import ReportsAddDataStoryTour from 'webapp/models-custom/tour/reports/add-data-story';
import ReportsAddNoteTour from 'webapp/models-custom/tour/reports/add-note';
import ReportsChangeDataStorySettingsTour from 'webapp/models-custom/tour/reports/change-data-story-settings';
import ReportsCreateNewSlideTour from 'webapp/models-custom/tour/reports/create-new-slide';
import ReportsDownloadPdfTour from 'webapp/models-custom/tour/reports/download-pdf';
import ReportsFavoriteTour from 'webapp/models-custom/tour/reports/favorite';
import ReportsFirstPreviewTour from 'webapp/models-custom/tour/reports/first-preview';
import ReportsPagelessAddBlocksTour from 'webapp/models-custom/tour/reports/pageless-add-blocks';
import ReportsPagelessAddColumnTour from 'webapp/models-custom/tour/reports/pageless-add-column';
import ReportsPagelessCustomizeTour from 'webapp/models-custom/tour/reports/pageless-customize';
import ReportsPagelessShareTour from 'webapp/models-custom/tour/reports/pageless-share';
import ReportsPagelessViewSwitchTour from 'webapp/models-custom/tour/reports/pageless-view-switch';
import ReportsPlansGrowthTour from 'webapp/models-custom/tour/reports/plans-performer';
import ReportsPresentTour from 'webapp/models-custom/tour/reports/present';
import ReportsPreviewPopupEditTour from 'webapp/models-custom/tour/reports/preview-popup-edit';
import ReportsPreviewPopupShareTour from 'webapp/models-custom/tour/reports/preview-popup-share';
import ReportsReorderSlidesTour from 'webapp/models-custom/tour/reports/reorder-slides';
import ReportsSelectDataElementTour from 'webapp/models-custom/tour/reports/select-data-element';
import ReportsSelectDataStoryTour from 'webapp/models-custom/tour/reports/select-data-story';
import WhiteLabelingPayingCustomersPromoTour from 'webapp/models-custom/tour/white-labeling-paying-customers-promo';
import WizardDataboardIntroTour from 'webapp/models-custom/tour/wizard-databoard-intro';
import WizardFirstTour from 'webapp/models-custom/tour/wizard-first';
import WizardSecondTour from 'webapp/models-custom/tour/wizard-second';

// Notifications manifest
// https://docs.google.com/spreadsheets/d/1Lm2WnQ9ZJ-eIm77Tn5JgnRm5YGXdxy6AJ20LbBr87M4

export default class TourService extends EmberService {
	@service('account') declare accountService: Service.Account;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('logger') declare loggerService: Service.Logger;
	@service('request') declare requestService: Service.Request;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('billing') declare billingService: Service.Billing;
	@service('user-settings') declare userSettingsService: Service.UserSettings;

	tourVariables: Coredata.Main['tourValues'] = {};

	isEligibleForFirstBoardViewTour = false;

	get isEligibleForCustomMetricsPopupQbTour(): boolean {
		return this.isTourEligible(CustomMetricsPopupQbTour.keyForPersistantDone);
	}

	get isEligibleForCustomMetricsPopupScriptTour(): boolean {
		return this.isTourEligible(CustomMetricsPopupScriptTour.keyForPersistantDone);
	}

	get isEligibleForQbSheetsTour(): boolean {
		return this.isTourEligible(QbSheetsTour.keyForPersistantDone);
	}

	get isEligibleForClientPerformanceTour(): boolean {
		return this.isTourEligible(ClientPerformanceTour.keyForPersistantDone);
	}

	get isEligibleForFirstBoardEditTour(): boolean {
		return this.isTourEligible(FirstBoardEditTour.keyForPersistantDone);
	}

	get isEligibleForFirstBoardShareTour(): boolean {
		return this.isTourEligible(FirstBoardShareTour.keyForPersistantDone);
	}

	get isEligibleForMetricsSourcesTour(): boolean {
		return this.isTourEligible(MetricsSourcesTour.keyForPersistantDone);
	}

	get isEligibleForClientsSetupTour(): boolean {
		return this.isTourEligible(ClientsSetupTour.keyForPersistantDone);
	}

	get isEligibleForQbSheetsVideoTour(): boolean {
		return this.isTourEligible(QbSheetsVideoTour.keyForPersistantDone);
	}

	get isEligibleForWizardFirstTour(): boolean {
		return (
			this.userSettingsService.showFirstWizard &&
			this.isTourEligible(WizardFirstTour.keyForPersistantDone)
		);
	}

	get isEligibleForWizardSecondTour(): boolean {
		return (
			this.currentUserService.isSignedUpFromTemplate &&
			this.isTourEligible(WizardSecondTour.keyForPersistantDone)
		);
	}

	get isEligibleForWizardDataboardIntroTour(): boolean {
		return (
			this.currentUserService.isSignedUpFromTemplate &&
			this.isTourEligible(WizardDataboardIntroTour.keyForPersistantDone)
		);
	}

	get isEligibleForOnboardingSourceTour(): boolean {
		return this.isTourEligible(OnboardingSourceTour.keyForPersistantDone);
	}

	get isEligibleForReportsCreateNewSlide(): boolean {
		return this.isTourEligible(ReportsCreateNewSlideTour.keyForPersistantDone);
	}

	get isEligibleForReportsAddNoteTour(): boolean {
		return this.isTourEligible(ReportsAddNoteTour.keyForPersistantDone);
	}

	get isEligibleForReportsReorderSlidesTour(): boolean {
		return this.isTourEligible(ReportsReorderSlidesTour.keyForPersistantDone);
	}

	get isEligibleForReportsDownloadPdfTour(): boolean {
		return this.isTourEligible(ReportsDownloadPdfTour.keyForPersistantDone);
	}

	get isEligibleForReportsFirstPreviewTour(): boolean {
		return this.isTourEligible(ReportsFirstPreviewTour.keyForPersistantDone);
	}

	get isEligibleForReportsPreviewPopupEditTour(): boolean {
		return this.isTourEligible(ReportsPreviewPopupEditTour.keyForPersistantDone);
	}

	get isEligibleForReportsPreviewPopupShareTour(): boolean {
		return this.isTourEligible(ReportsPreviewPopupShareTour.keyForPersistantDone);
	}

	get isEligibleForReportsFavoriteTour(): boolean {
		return false; // Tour on hold // this.isTourEligible(ReportsFavoriteTour.keyForPersistantDone);
	}

	get isEligibleForReportsPresentTour(): boolean {
		return this.isTourEligible(ReportsPresentTour.keyForPersistantDone);
	}

	get isEligibleForReportsPagelessAddBlocksTour(): boolean {
		return this.isTourEligible(ReportsPagelessAddBlocksTour.keyForPersistantDone);
	}

	get isEligibleForReportsPagelessAddColumnTour(): boolean {
		return this.isTourEligible(ReportsPagelessAddColumnTour.keyForPersistantDone);
	}

	get isEligibleForReportsPagelessCustomizeTour(): boolean {
		return this.isTourEligible(ReportsPagelessCustomizeTour.keyForPersistantDone);
	}

	get isEligibleForReportsPagelessShareTour(): boolean {
		return this.isTourEligible(ReportsPagelessShareTour.keyForPersistantDone);
	}

	get isEligibleForReportsPagelessViewSwitchTour(): boolean {
		return this.isTourEligible(ReportsPagelessViewSwitchTour.keyForPersistantDone);
	}

	get isEligibleForDesignerIntroTour(): boolean {
		return this.isTourEligible(DesignerIntroTour.keyForPersistantDone);
	}

	get isEligibleForDesignerAddToLoopDataboardTour(): boolean {
		return this.isTourEligible(DesignerAddToLoopDataboardTour.keyForPersistantDone);
	}

	get isEligibleForDesignerAddToReportTour(): boolean {
		return this.isTourEligible(DesignerAddToReportTour.keyForPersistantDone);
	}

	get isEligibleForDesignerChangeDateRangeTour(): boolean {
		return this.isTourEligible(DesignerChangeDateRangeTour.keyForPersistantDone);
	}

	get isEligibleForDesignerDragAndDropTour(): boolean {
		return this.isTourEligible(DesignerDragAndDropTour.keyForPersistantDone);
	}

	get isEligibleForDesignerMoveBlockTour(): boolean {
		return this.isTourEligible(DesignerMoveBlockTour.keyForPersistantDone);
	}

	get isEligibleForDesignerResizeBlockTour(): boolean {
		return this.isTourEligible(DesignerResizeBlockTour.keyForPersistantDone);
	}

	get isEligibleForDesignerSelectVisualizationTypeTour(): boolean {
		return this.isTourEligible(DesignerSelectVisualizationTypeTour.keyForPersistantDone);
	}

	get isEligibleForDesignerSelectDimensionTour(): boolean {
		return this.isTourEligible(DesignerSelectDimensionTour.keyForPersistantDone);
	}

	get isEligibleForSharableLinkTour(): boolean {
		return this.isTourEligible(DesignerSharableLinkTour.keyForPersistantDone);
	}

	get isEligibleForMoreOptionsTour(): boolean {
		return this.isTourEligible(DesignerMoreOptionTour.keyForPersistantDone);
	}

	get isEligibleForReportsPlansGrowthTour(): boolean {
		return this.isTourEligible(ReportsPlansGrowthTour.keyForPersistantDone);
	}

	get isEligibleForReportsAddDataStoryTour(): boolean {
		return this.isTourEligible(ReportsAddDataStoryTour.keyForPersistantDone);
	}

	get isEligibleForReportsSelectDataStoryTour(): boolean {
		return this.isTourEligible(ReportsSelectDataStoryTour.keyForPersistantDone);
	}

	get isEligibleForReportsChangeDataStorySettingsTour(): boolean {
		return this.isTourEligible(ReportsChangeDataStorySettingsTour.keyForPersistantDone);
	}

	get isEligibleForReportsAddDataElementTour(): boolean {
		return this.isTourEligible(ReportsAddDataElementTour.keyForPersistantDone);
	}

	get isEligibleForReportsSelectDataElementTour(): boolean {
		return this.isTourEligible(ReportsSelectDataElementTour.keyForPersistantDone);
	}

	get currentEligibleReportsDataStoryTourId(): string {
		if (this.billingService.visualizationsAdvancedEnabled) {
			if (this.isEligibleForReportsAddDataStoryTour) {
				return TourId.REPORTS__ADD_DATA_STORY;
			} else if (this.isEligibleForReportsSelectDataStoryTour) {
				return TourId.REPORTS__SELECT_DATA_STORY;
			} else if (this.isEligibleForReportsChangeDataStorySettingsTour) {
				return TourId.REPORTS__CHANGE_DATA_STORY_SETTINGS;
			} else if (this.isEligibleForReportsAddDataElementTour) {
				return TourId.REPORTS__ADD_DATA_ELEMENT;
			} else if (this.isEligibleForReportsSelectDataElementTour) {
				return TourId.REPORTS__SELECT_DATA_ELEMENT;
			}
		}
		return '';
	}

	get isReportsDataStoryTourInProgress(): boolean {
		return (
			!this.isEligibleForReportsAddDataStoryTour &&
			this.isEligibleForReportsSelectDataElementTour
		);
	}

	get isEligibleForWhitelabelingPayingCustomersPromoTour(): boolean {
		return this.isTourEligible(WhiteLabelingPayingCustomersPromoTour.keyForPersistantDone);
	}

	get isEligibleForRecentlyDeletedTour(): boolean {
		return this.isTourEligible(RecentlyDeletedTour.keyForPersistantDone);
	}

	isTourEligible(id: string): boolean {
		return !this.tourVariables[id];
	}

	private setVariable(key: string, value: boolean) {
		// true means done
		this.tourVariables[key] = value;
	}

	doRequest(action: string, key: UserSettingsKey): void {
		if (action === 'reset') {
			void this.userSettingsService.delete(key, true);
		} else {
			void this.userSettingsService.save(key, true, true).then(() => {
				this.setVariable(key, action === 'done');
			});
		}
	}

	// Tour and TourReact both leave markPersistant a no-op — the service owns persistence, so every
	// tour instance it hands out gets the real implementation injected here.
	private checkPersistance<T extends Tour | TourReact>(tour: T): T {
		tour.markPersistant = (action: string, key: UserSettingsKey) => {
			// skip if done already
			if (!(action === 'done' && this.tourVariables[key])) {
				this.doRequest(action, key);
			}
		};

		return tour;
	}

	initTour(id: string, opts: Record<string, unknown> = {}): Tour | null {
		if (this.navigationService.isMobileApp || this.accountService.isDemoAccount) {
			return null;
		}

		let tour;

		switch (id) {
			case TourId.CUSTOM_METRICS_POPUP_QB:
				tour = new CustomMetricsPopupQbTour(opts);
				break;
			case TourId.CUSTOM_METRICS_POPUP_SCRIPT:
				tour = new CustomMetricsPopupScriptTour(opts);
				break;
			case TourId.QB_SHEETS:
				tour = new QbSheetsTour(opts);
				break;
			case TourId.CLIENT_PERFORMANCE:
				tour = new ClientPerformanceTour(opts);
				break;
			case TourId.FIRST_BOARD_VIEW:
				tour = new FirstBoardViewTour(opts);
				break;
			case TourId.FIRST_BOARD_EDIT:
				tour = new FirstBoardEditTour(opts);
				break;
			case TourId.FIRST_BOARD_SHARE:
				tour = new FirstBoardShareTour(opts);
				break;
			case TourId.METRICS_SOURCES:
				tour = new MetricsSourcesTour(opts);
				break;
			case TourId.ONBOARDING_SOURCE:
				tour = new OnboardingSourceTour(opts);
				break;
			case TourId.CLIENTS_SETUP:
				tour = new ClientsSetupTour(opts);
				break;
			case TourId.QB_SHEETS_VIDEO:
				tour = new QbSheetsVideoTour(opts);
				break;
			case TourId.WIZARD_FIRST:
				tour = new WizardFirstTour(opts);
				break;
			case TourId.WIZARD_SECOND:
				tour = new WizardSecondTour(opts);
				break;
			case TourId.WIZARD_DATABOARD_INTRO:
				tour = new WizardDataboardIntroTour(opts);
				break;
			case TourId.REPORTS__CREATE_NEW_SLIDE:
				tour = new ReportsCreateNewSlideTour(opts);
				break;
			case TourId.REPORTS__ADD_NOTE:
				tour = new ReportsAddNoteTour(opts);
				break;
			case TourId.REPORTS__REORDER_SLIDES:
				tour = new ReportsReorderSlidesTour(opts);
				break;
			case TourId.REPORTS__DOWNLOAD_PDF:
				tour = new ReportsDownloadPdfTour(opts);
				break;
			case TourId.REPORTS__FIRST_PREVIEW:
				tour = new ReportsFirstPreviewTour(opts);
				break;
			case TourId.REPORTS__PREVIEW_POPUP_EDIT:
				tour = new ReportsPreviewPopupEditTour(opts);
				break;
			case TourId.REPORTS__PREVIEW_POPUP_SHARE:
				tour = new ReportsPreviewPopupShareTour(opts);
				break;
			case TourId.REPORTS__FAVORITE:
				tour = new ReportsFavoriteTour(opts);
				break;
			case TourId.REPORTS__PRESENT:
				tour = new ReportsPresentTour(opts);
				break;
			case TourId.REPORTS__PAGELESS_ADD_BLOCKS:
				tour = new ReportsPagelessAddBlocksTour(opts);
				break;
			case TourId.REPORTS__PAGELESS_ADD_COLUMN:
				tour = new ReportsPagelessAddColumnTour(opts);
				break;
			case TourId.REPORTS__PAGELESS_CUSTOMIZE:
				tour = new ReportsPagelessCustomizeTour(opts);
				break;
			case TourId.REPORTS__PAGELESS_SHARE:
				tour = new ReportsPagelessShareTour(opts);
				break;
			case TourId.REPORTS__PAGELESS_VIEW_SWITCH:
				tour = new ReportsPagelessViewSwitchTour(opts);
				break;
			case TourId.DESIGNER__INTRO:
				tour = new DesignerIntroTour(opts);
				break;
			case TourId.DESIGNER__ADD_TO_LOOP:
				tour = new DesignerAddToLoopDataboardTour(opts);
				break;
			case TourId.DESIGNER__ADD_TO_REPORT:
				tour = new DesignerAddToReportTour(opts);
				break;
			case TourId.DESIGNER__CHANGE_DATE_RANGE:
				tour = new DesignerChangeDateRangeTour(opts);
				break;
			case TourId.DESIGNER__DRAG_N_DROP:
				tour = new DesignerDragAndDropTour(opts);
				break;
			case TourId.DESIGNER__MOVE_BLOCK:
				tour = new DesignerMoveBlockTour(opts);
				break;
			case TourId.DESIGNER__RESIZE_BLOCK:
				tour = new DesignerResizeBlockTour(opts);
				break;
			case TourId.DESIGNER__SELECT_VISUALIZATION:
				tour = new DesignerSelectVisualizationTypeTour(opts);
				break;
			case TourId.DESIGNER__SELECT_DIMENSION:
				tour = new DesignerSelectDimensionTour(opts);
				break;
			case TourId.DESIGNER__BOARD_MORE_MENU:
				tour = new DesignerOptionHiddenTour(opts);
				break;
			case TourId.DESIGNER__SHARABLE_LINK:
				tour = new DesignerSharableLinkTour(opts);
				break;
			case TourId.DESIGNER__MORE_OPTION:
				tour = new DesignerMoreOptionTour(opts);
				break;
			case TourId.REPORTS__PLAN_PERFORMER:
				tour = new ReportsPlansGrowthTour(opts);
				break;
			case TourId.REPORTS__ADD_DATA_STORY:
				tour = new ReportsAddDataStoryTour(opts);
				break;
			case TourId.REPORTS__SELECT_DATA_STORY:
				tour = new ReportsSelectDataStoryTour(opts);
				break;
			case TourId.REPORTS__CHANGE_DATA_STORY_SETTINGS:
				tour = new ReportsChangeDataStorySettingsTour(opts);
				break;
			case TourId.REPORTS__ADD_DATA_ELEMENT:
				tour = new ReportsAddDataElementTour(opts);
				break;
			case TourId.REPORTS__SELECT_DATA_ELEMENT:
				tour = new ReportsSelectDataElementTour(opts);
				break;
			case TourId.WHITE_LABELING_PAYING_CUSTOMERS_PROMO:
				tour = new WhiteLabelingPayingCustomersPromoTour(opts);
				break;
			case TourId.ONBOARDING_CONNECT_SOURCE:
				tour = new OnboardingConnectSourceTour(opts);
				break;
			case TourId.GUIDED_ONBOARDING__METRICS:
				tour = new GuidedOnboardingMetricsTour(opts);
				break;
			case TourId.GUIDED_ONBOARDING__WIZARD:
				tour = new GuidedOnboardingWizardTour(opts);
				break;
			case TourId.GUIDED_ONBOARDING__DATA_MANAGER_SELECTED:
				tour = new GuidedOnboardingDataManagerSelectedTour(opts);
				break;
			case TourId.GUIDED_ONBOARDING__DATA_MANAGER_POPULAR:
				tour = new GuidedOnboardingDataManagerPopularTour(opts);
				break;
			case TourId.RECENTLY_DELETED:
				tour = new RecentlyDeletedTour(opts);
				break;
			default:
				this.loggerService.logError('Nonexistant tour requested', { id });
				return null;
		}

		return this.checkPersistance(tour);
	}

	private startedReactTours = new Set<string>();

	private get reactTours(): TourReact[] {
		const tours = [
			this.checkPersistance(new GenieClientSelectorTour()),
			this.checkPersistance(new GenieCustomizeTour()),
			this.checkPersistance(new GenieArtifactEditTour()),
		];

		if (!this.accountService.isClient) {
			tours.push(this.checkPersistance(new GenieSkillsTour()));
		}

		return tours;
	}

	// Every tour the user is still eligible for, in priority order. React shows the first whose
	// anchor is registered, so a tour whose anchor is not on the current surface no longer blocks
	// the ones behind it — and needs no "unavailable" round trip to say so.
	getPendingGenieTours(): TourReactPayload[] {
		if (this.navigationService.isMobileApp || this.accountService.isDemoAccount) {
			return [];
		}

		return this.reactTours
			.filter((tour) => this.isTourEligible(tour.keyForPersistantDone))
			.map((tour) => tour.toPayload());
	}

	// Reports the tour's analytics event, once. The Genie mount re-renders React on
	// accessTokenRefreshed and themeChanged, so React can announce the same tour more than once per
	// session — firing per re-render would silently inflate the metric.
	announceGenieTour(tourId: string): void {
		if (this.startedReactTours.has(tourId)) {
			return;
		}

		const tour = this.reactTours.find((reactTour) => reactTour.id === tourId);

		if (!tour) {
			return;
		}

		this.startedReactTours.add(tourId);
		tour.start();
	}

	completeGenieTour(tourId: string): void {
		this.reactTours.find((tour) => tour.id === tourId)?.markDone();
	}

	// Every React-rendered surface (nav sidebar, full-screen Genie) wires the same tour callbacks
	// into the `tour` prop its entry point expects — build it once here instead of duplicating the
	// object at each call site. `pending` is read fresh on every call, so re-renders stay in sync
	// with tour state.
	// `onlyTourIds` narrows a surface to the tours it owns, so a surface that didn't take part in the Genie
	// tours before (the side panel) doesn't start showing the others.
	getGenieTourConfig(onlyTourIds?: readonly TourId[]): TourConfig {
		const pending = this.getPendingGenieTours();

		return {
			pending: onlyTourIds
				? pending.filter((tour) => onlyTourIds.some((id) => id === tour.id))
				: pending,
			onShown: (tourId: string) => this.announceGenieTour(tourId),
			onComplete: (tourId: string) => this.completeGenieTour(tourId),
			onSkip: (tourId: string) => this.completeGenieTour(tourId),
		};
	}

	markToursAsDone(tourKeys: readonly UserSettingsKey[]): void {
		tourKeys.forEach((key) => {
			this.doRequest('done', key);
		});
	}
}

declare module '@ember/service' {
	interface Registry {
		tour: TourService;
	}
}
